/**
 * Service for fetching default hints from the backend.
 */

import apiService from './api';

export interface FeedbackLoopHint {
  enhancement_instruction?: string;
  validation_rules?: string;
  focus_areas?: string;
  leniency?: 'strict' | 'moderate' | 'generous';
  iteration_context?: string;
}

export interface DefaultHintsResponse {
  step_type: string;
  default_hints: Record<string, FeedbackLoopHint>;
}

export interface AllDefaultHintsResponse {
  validator_hints: Record<string, FeedbackLoopHint>;
  feedback_receiver_hints: Record<string, FeedbackLoopHint>;
  applicable_step_types: {
    validator: string[];
    feedback_receiver: string[];
  };
}

class HintDefaultsService {
  /**
   * Fetch default hints for a specific step type.
   */
  async getDefaultHints(stepType: string): Promise<DefaultHintsResponse> {
    try {
      const response = await apiService.get(`/api/v1/hint-defaults/${stepType}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching default hints for ${stepType}:`, error);
      throw error;
    }
  }

  /**
   * Fetch all default hints for all step types.
   */
  async getAllDefaultHints(): Promise<AllDefaultHintsResponse> {
    try {
      const response = await apiService.get('/api/v1/hint-defaults');
      return response.data;
    } catch (error) {
      console.error('Error fetching all default hints:', error);
      throw error;
    }
  }

  /**
   * Get the appropriate default hints for a step type.
   * This method includes built-in fallbacks if the API is unavailable.
   */
  async getDefaultHintsWithFallback(stepType: string): Promise<Record<string, FeedbackLoopHint>> {
    try {
      const response = await this.getDefaultHints(stepType);
      // Transform keys from backend format to UI format
      const transformedHints = this.transformHintKeys(response.default_hints);
      return transformedHints;
    } catch (error) {
      console.warn('Using fallback default hints due to API error:', error);
      const fallbackHints = this.getFallbackHints(stepType);
      return fallbackHints;
    }
  }

  /**
   * Transform hint keys from backend format (iteration_1) to UI format (1)
   */
  private transformHintKeys(hints: Record<string, FeedbackLoopHint>): Record<string, FeedbackLoopHint> {
    const transformed: Record<string, FeedbackLoopHint> = {};
    
    for (const [key, value] of Object.entries(hints)) {
      // Transform iteration_1 -> 1, iteration_2 -> 2, iteration_3+ -> 3+
      const transformedKey = key.replace('iteration_', '');
      transformed[transformedKey] = value;
    }
    
    return transformed;
  }

  /**
   * Fallback hints in case the API is unavailable.
   * These should match the backend defaults but serve as a backup.
   * Note: stepType can be in format "run_agent:validate" to indicate intent
   */
  private getFallbackHints(stepType: string): Record<string, FeedbackLoopHint> {
    // Check if stepType includes intent (e.g., "run_agent:validate")
    const isValidateIntent = stepType === 'run_agent:validate' || stepType.includes('validate');

    if (isValidateIntent) {
      return {
        '1': {
          enhancement_instruction: "Evaluate the output against the requirements. Provide a balanced assessment with specific, actionable feedback for any areas needing improvement.",
          validation_rules: "Be specific and constructive. Focus on what needs improvement and how to achieve it.",
          leniency: 'strict'
        },
        '2': {
          enhancement_instruction: "This is iteration 2. The output has been revised based on your previous feedback. Assess the improvements that have been made. If you provided a score previously, consider whether the changes warrant an improved score.",
          validation_rules: "IMPORTANT: Acknowledge improvements where they exist. Your assessment should reflect positive progress if changes address your previous feedback. Focus remaining critique only on significant unresolved issues. Your scoring should improve if the output has meaningfully addressed your feedback.",
          focus_areas: "Previous feedback was: {previous_feedback}. Check if these specific concerns have been addressed.",
          leniency: 'moderate'
        },
        '3+': {
          enhancement_instruction: "This is iteration {iteration}. Multiple revisions have been made based on feedback. Evaluate with consideration for the iterative improvements.",
          validation_rules: "IMPORTANT: Your evaluation should heavily weight the cumulative improvements made. If the output substantially addresses the requirements, your assessment should be predominantly positive. Reserve critical feedback only for issues that fundamentally compromise the output's usability.",
          focus_areas: "Previous feedback: {previous_feedback}. Assess whether this has been reasonably addressed.",
          leniency: 'generous',
          iteration_context: "Consider that perfection may not be achievable or necessary. Focus your evaluation on whether the output is sufficiently good for its intended purpose."
        }
      };
    } else if (stepType === 'run_agent' || stepType.startsWith('run_agent:')) {
      return {
        '1': {
          enhancement_instruction: "Generate content according to the requirements. Focus on completeness and quality.",
          validation_rules: "Ensure your output addresses all specified requirements comprehensively."
        },
        '2': {
          enhancement_instruction: "This is iteration 2. You have received feedback on your previous output. Use this feedback to improve your response.",
          focus_areas: "Previous feedback was: {previous_feedback}. Address these specific points in your revised output.",
          validation_rules: "IMPORTANT: Your revised output should directly address the feedback provided. Make meaningful improvements based on the specific suggestions."
        },
        '3+': {
          enhancement_instruction: "This is iteration {iteration}. You have received multiple rounds of feedback.",
          focus_areas: "Latest feedback: {previous_feedback}. Focus on addressing any remaining issues.",
          validation_rules: "Your output should demonstrate clear improvements based on all previous feedback. Ensure all major concerns have been addressed.",
          iteration_context: "You have made multiple revisions. Focus on resolving any critical remaining issues while maintaining the improvements already made."
        }
      };
    }

    return {};
  }
}

export const hintDefaultsService = new HintDefaultsService();